/**
 * VideoCallLastGift — the transient "last sent gift" overlay of the video-call
 * page. A thin wrapper around the shared `LastGiftOverlay`
 * (`components/common/LastGiftOverlay.tsx`): it owns only the video-call-specific
 * copy source (`l10n.chat.videoCallPage`), the position style, and the qa id —
 * every visual detail lives in the shared component. Ports Flutter's `_lastGift()`
 * widget (`video_call_screen.dart:551-691`): a left-edge, vertically-centred
 * banner shown whenever a gift is sent during the call.
 *
 * This wrapper is PURE and prop-driven: the page (`VideoCallPage`) owns the
 * current-user decision and passes `isCurrentUserSender` in — this component only
 * substitutes the "You" string (Flutter computes `username = "You" | userName`
 * inline at `:562-566`; here the page decides and this wrapper substitutes).
 *
 * Position: Flutter `Positioned(left: 0, bottom: height/2)` (`:552-554`) — left
 * edge, vertically centred. Web: `position: absolute; left: 0; top: 50%;
 * transform: translateY(-50%)` with `paddingLeft: responsive.spacing.lg`, passed
 * as the shared component's `style` override.
 */

import React from 'react';

import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { LastGiftOverlay } from '@/presentation/components/common/LastGiftOverlay';
import type { LatestLiveGiftInfoEntity } from '@/domain/entities/info/latestLiveGiftInfoEntity';

export interface VideoCallLastGiftProps {
  /**
   * The gift to display, or `null` to render nothing (Flutter `:558-560`). Mirrors
   * the `latestLiveGift` view-model field exposed on `UseVideoCallResult`.
   */
  gift: LatestLiveGiftInfoEntity | null;
  /**
   * Current fade opacity (Flutter `_latestLiveGiftOpacity`, `:571`). 1 while the
   * gift is shown, 0 once the display window elapses. Mirrors the
   * `latestLiveGiftOpacity` view-model field.
   */
  opacity: number;
  /**
   * Whether the gift's sender is the current user (Flutter `:562-566` computes
   * `userId == currentFirebaseUser.uid`). The page owns this decision; this
   * component only substitutes the "You" string when `true`.
   */
  isCurrentUserSender: boolean;
}

export const VideoCallLastGift: React.FC<VideoCallLastGiftProps> = ({
  gift,
  opacity,
  isCurrentUserSender,
}) => {
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const copy = l10n.chat.videoCallPage;

  // Title: "You sent a gift" for the current user, "{userName} sent a gift"
  // otherwise (Flutter `:562-566, :615`). `gift?.userName` guards the null case —
  // the shared overlay early-returns `null` when `gift == null`, so the built
  // title is unused there.
  const senderName = isCurrentUserSender ? copy.you : (gift?.userName ?? '');
  const title = copy.sentGift.replace('{name}', senderName);

  return (
    <LastGiftOverlay
      gift={gift}
      opacity={opacity}
      title={title}
      qaId="video-call-last-gift"
      style={{
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        paddingLeft: responsive.spacing.lg,
      }}
    />
  );
};
